import React from "react";
import {
  List,
  DataTable,
  NumberField,
  TextField,
  Edit,
  Create,
  TabbedForm,
  ArrayInput,
  TextInput,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  Button,
  EditButton,
  SimpleFormIterator,
  AutocompleteInput,
  CheckboxGroupInput,
  useRecordContext,
  required,
  FormDataConsumer,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  regex,
  FilterButton,
} from "react-admin";
import { Link } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material//ArrowForward";
import InfoIcon from "@mui/icons-material/Info";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

const validateRegExp = (value) => {
  try {
    new RegExp(value);
  } catch (error) {
    return "" + error;
  }
  return undefined;
};

const LoginButton = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Button
      label="Login"
      component={Link}
      to={`/c/${record.id}/`}
      target="_blank"
    >
      <ArrowForwardIcon />
    </Button>
  );
};

const CustomerAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {"Customer #" + record.id + ": " + record.longName}</span>;
};

const SelectBillingStatus = ({ allowMulti, ...props }: any) => {
  const Elem = allowMulti ? CheckboxGroupInput : RadioButtonGroupInput;

  return (
    <Elem
      {...props}
      choices={[
        { id: "subscribed", name: "Subscribed" },
        { id: "internal", name: "Internal" },
        { id: "dormant", name: "Dormant" },
      ]}
    />
  );
};

const customerFilters = [
  <SelectBillingStatus
    label="Status"
    source="billingStatus"
    allowMulti
    style={{ marginBottom: 0, flexDirection: "row" }}
  />,
];

export const CustomerList = () => (
  <List
    pagination={null}
    filters={customerFilters}
    filterDefaultValues={{ billingStatus: ["subscribed", "internal"] }}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="id" label="ID" disableSort>
        <TextField source="id" label="ID" />
      </DataTable.Col>
      <DataTable.Col source="shortName" label="Short Name" disableSort>
        <TextField source="shortName" label="Short Name" />
      </DataTable.Col>
      <DataTable.Col source="longName" label="Long Name" disableSort>
        <TextField source="longName" label="Long Name" />
      </DataTable.Col>
      <DataTable.Col source="region" label="Region" disableSort>
        <TextField source="region" label="Region" />
      </DataTable.Col>
      <DataTable.Col source="billingStatus" label="Status" disableSort>
        <TextField source="billing.status" label="Status" />
      </DataTable.Col>
      <DataTable.Col>
        <EditButton />
      </DataTable.Col>
      <DataTable.Col>
        <LoginButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const AccessLevel = [
  { id: "member", name: "member" },
  { id: "manager", name: "manager" },
  { id: "technician", name: "technician" },
];

const UserAssociationInput = () => {
  const record = useRecordContext();
  return (
    <ArrayInput source="userAssociations">
      <SimpleFormIterator source="users" inline>
        {record ? (
          <NumberField
            source="customerID"
            defaultValue={record.id}
            hidden
          ></NumberField>
        ) : null}
        <ReferenceInput
          source="userID"
          reference="users"
          sort={{ field: "email", order: "ASC" }}
        >
          <AutocompleteInput
            optionText="email"
            filterToQuery={(searchText) => ({ email: searchText })}
            label="User"
            style={{ width: "min(400px, 100%)" }}
          />
        </ReferenceInput>
        <SelectInput
          source="access"
          choices={AccessLevel}
          label="Access Level"
          defaultValue={"member"}
          validate={required()}
        />
      </SimpleFormIterator>
    </ArrayInput>
  );
};

const TaskType = [
  { id: "inbound", name: "Inbound" },
  { id: "inbound_ferment_analysis", name: "Inbound - Ferment Analysis" },
  { id: "outbound_virtual_composite", name: "Outbound - Virtual Composite" },
];
const validateCron = regex(
  /^(?:\d{1,2}|\*) (?:\d{1,2}|\*) (?:\d{1,2}|\*) (?:\d{1,2}|\*) (?:\d{1,2}|\*)$/,
  "Must be a valid cron expression",
);
const ScheduleField = () => (
  <>
    <div style={{ display: "flex", alignItems: "center" }}>
      <h3 style={{ marginRight: "10px" }}>Schedule</h3>
      <Tooltip title="Cron" placement="top">
        <IconButton
          href="https://en.wikipedia.org/wiki/Cron"
          target="_blank"
          rel="noopener noreferrer"
        >
          <InfoIcon />
        </IconButton>
      </Tooltip>
    </div>
    <ArrayInput source="integration.schedule" label="">
      <SimpleFormIterator inline disableReordering>
        <SelectInput source="type" label="Type" choices={TaskType} />
        <TextInput source="cron" label="Cron" validate={validateCron} />
      </SimpleFormIterator>
    </ArrayInput>
  </>
);

const IntegrationSetting = () => {
  return (
    <>
      <SelectInput
        label="Type"
        source="integration.type"
        choices={[
          { id: "innovint", name: "InnoVint" },
          { id: "vintrace", name: "Vintrace" },
        ]}
      />
      <FormDataConsumer>
        {({ formData }) => {
          if (formData.integration?.type === "innovint")
            return (
              <>
                <TextInput
                  source="integration.innoVintPublicWineryId"
                  label="Public Winery ID"
                  style={{ width: "min(300px, 100%)" }}
                />
                <TextInput
                  source="integration.innoVintPublicOrgId"
                  label="Public Organization ID (required for Lot Intended Use)"
                  style={{ width: "min(300px, 100%)" }}
                />
                <TextInput
                  source="integration.innoVintPAT"
                  label="Personal Access Token (PAT)"
                  style={{ width: "min(300px, 100%)" }}
                />
                <ScheduleField />
              </>
            );
          else if (formData.integration?.type === "vintrace")
            return (
              <>
                <TextInput
                  source="integration.vintraceBaseUrl"
                  label="Base URL"
                  placeholder="https://abc123.vintrace.net"
                  style={{ width: "min(300px, 100%)" }}
                />
                <TextInput
                  source="integration.vintraceCustomerCode"
                  label="Customer Code"
                  style={{ width: "min(300px, 100%)" }}
                />
                <TextInput
                  source="integration.vintraceAPIToken"
                  label="API Token"
                  style={{ width: "min(300px, 100%)" }}
                />
                <TextInput
                  source="integration.vintraceBusinessUnit"
                  label="Business Unit (optional)"
                  placeholder="US42"
                  style={{ width: "min(300px, 100%)" }}
                />
                <NumberInput
                  source="integration.vintraceWineryId"
                  label="Winery ID (optional)"
                  style={{ width: "min(300px, 100%)" }}
                />
                <ScheduleField />
              </>
            );
        }}
      </FormDataConsumer>
    </>
  );
};

const CreateEditForm = ({ disableShortname, ...props }: any) => (
  <TabbedForm {...props}>
    <TabbedForm.Tab label="General">
      <NumberInput label="ID" source="id" readOnly />
      <TextInput
        label="Short Name"
        source="shortName"
        readOnly={disableShortname}
      />
      <TextInput label="Long Name" source="longName" />
      <TextInput label="Region" source="region" />
    </TabbedForm.Tab>
    <TabbedForm.Tab label="Billing">
      <SelectBillingStatus label="Status" source="billing.status" />
      <TextInput multiline fullWidth label="Notes" source="billing.notes" />
    </TabbedForm.Tab>
    <TabbedForm.Tab label="Preferences">
      <RadioButtonGroupInput
        label="Temperature Unit"
        source="preferences.temperatureUnit"
        choices={[
          { id: "°C", name: "°C" },
          { id: "°F", name: "°F" },
        ]}
      />
      <RadioButtonGroupInput
        label="Volume Unit"
        source="preferences.volumeUnit"
        choices={[
          { id: "L", name: "Litre" },
          { id: "Gal", name: "Gallon" },
        ]}
      />
      <RadioButtonGroupInput
        label="Density Unit"
        source="preferences.densityUnit"
        choices={[
          { id: "°Bx", name: "Brix (°Bx)" },
          { id: "°Oe", name: "Oechsle (°Oe)" },
          { id: "g/L", name: "SI (g/L)" },
        ]}
      />

      <TextInput
        fullWidth
        label="Barcode RegEx"
        source="preferences.barcodeRegex"
        validate={validateRegExp}
        helperText="Barcode RegEx is applied by the FS1 mobile app when scanning barcodes. Instead of the full barcode data, only the first match group is used."
      />

      <TextInput
        fullWidth
        label="Water Use Dashboard"
        source="preferences.iwmDashboard"
        placeholder="https://grafana.barrelwise.ca/d/bduu226fl3f28f/interactive-water-meters-usage?orgId=8&"
        helperText="The base url of a customer-accessible water use grafana dashboard. If blank, admin users will see their dashboard but regular users will not."
      />
    </TabbedForm.Tab>
    <TabbedForm.Tab label="Integration">
      <IntegrationSetting />
    </TabbedForm.Tab>
    <TabbedForm.Tab label="Users">
      <UserAssociationInput />
    </TabbedForm.Tab>
  </TabbedForm>
);

export const CustomerEdit = () => (
  <Edit redirect="list" title={<CustomerAppBarTitle />}>
    <CreateEditForm disableShortname={true} />
  </Edit>
);

export const CustomerCreate = () => (
  <Create redirect="list">
    <CreateEditForm />
  </Create>
);
